import { useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { formatItalianDate } from "@/lib/dateFormat";

// All circumferences consolidated in one tab, with a distinguishable earthy palette.
const CIRC_LINES_MALE = [
  { key: "avambraccio", label: "Avambraccio", color: "#A65D47" },
  { key: "braccio", label: "Braccio", color: "#B08C4D" },
  { key: "torace", label: "Torace", color: "#9C5F8A" },
  { key: "giro_vita", label: "Giro vita", color: "#8B4A2F" },
  { key: "periombelicale", label: "Periombelicale", color: "#7A4A5C" },
  { key: "giro_coscia", label: "Giro coscia", color: "#63705B" },
  { key: "vasto_mediale", label: "Vasto mediale", color: "#4A6274" },
  { key: "polpaccio", label: "Polpaccio", color: "#3F6F6F" },
];

const CIRC_LINES_FEMALE = [
  { key: "avambraccio", label: "Avambraccio", color: "#A65D47" },
  { key: "braccio", label: "Braccio", color: "#B08C4D" },
  { key: "giro_vita", label: "Giro vita", color: "#8B4A2F" },
  { key: "periombelicale", label: "Periombelicale", color: "#7A4A5C" },
  { key: "glutei", label: "Glutei", color: "#9C5F8A" },
  { key: "giro_coscia", label: "Giro coscia", color: "#63705B" },
  { key: "vasto_mediale", label: "Vasto mediale", color: "#4A6274" },
  { key: "polpaccio", label: "Polpaccio", color: "#3F6F6F" },
];

const COMP_LINES = [
  { key: "peso_kg", label: "Peso (kg)", color: "#A65D47" },
  { key: "massa_magra_kg", label: "Massa magra (kg)", color: "#63705B" },
  { key: "bodyfat_display", label: "Body fat (%)", color: "#B08C4D" },
];

// Static style objects hoisted out of the component so React.memo /
// referential equality checks don't break on every render.
const CHART_WRAPPER_STYLE = { width: "100%", height: 340, minWidth: 200 };
const CHART_MARGIN = { top: 10, right: 20, left: -5, bottom: 0 };
const TOOLTIP_CONTENT_STYLE = { background: "#FCFAF8", border: "1px solid #D7D2CB", borderRadius: 8, fontSize: 12 };
const TOOLTIP_LABEL_STYLE = { fontFamily: "Cormorant Garamond, serif", fontSize: 14 };
const LINE_DOT = { r: 3 };
const LINE_ACTIVE_DOT = { r: 5 };
const TABS = [
  { k: "composizione", label: "Composizione" },
  { k: "circonferenze", label: "Circonferenze" },
];

// Suffix each metric key uses when rendered in the custom legend (mostly
// centimeters for circumferences; body composition already exposes its own
// unit in the label).
const UNIT_BY_KEY = {
  peso_kg: "kg",
  massa_magra_kg: "kg",
  bodyfat_display: "%",
};

export default function MeasurementsChart({ data, gender = "M" }) {
  const [tab, setTab] = useState("composizione");
  // Per-tab set of hidden line keys — clicking a legend card toggles it.
  const [hiddenByTab, setHiddenByTab] = useState({ composizione: new Set(), circonferenze: new Set() });
  const hidden = hiddenByTab[tab] || new Set();
  const toggleLine = (k) => setHiddenByTab((state) => {
    const next = new Set(state[tab] || []);
    if (next.has(k)) next.delete(k); else next.add(k);
    return { ...state, [tab]: next };
  });

  const chartData = useMemo(
    () => (data || []).map((d) => ({
      ...d,
      dateLabel: formatItalianDate(d.date),
      bodyfat_display: +(d.bodyfat_pct * 100).toFixed(1),
    })),
    [data]
  );

  if (chartData.length === 0) {
    return (
      <div className="border border-dashed border-[color:var(--border)] rounded-2xl p-8 text-center text-sm text-[color:var(--text-secondary)]">
        Nessun rilievo ancora registrato.
      </div>
    );
  }

  const circLines = gender === "F" ? CIRC_LINES_FEMALE : CIRC_LINES_MALE;
  const metrics = tab === "circonferenze" ? circLines : COMP_LINES;

  return (
    <div className="border border-[color:var(--border)] rounded-2xl p-5 bg-[color:var(--bg-primary)]" data-testid="measurements-chart">
      <div className="flex flex-wrap gap-1 mb-4" data-testid="chart-tabs">
        {TABS.map((t) => (
          <button
            key={t.k}
            data-testid={`chart-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className="text-xs px-3 py-1 rounded-full border transition-colors"
            style={{
              borderColor: "var(--border)",
              background: tab === t.k ? "var(--text-primary)" : "transparent",
              color: tab === t.k ? "var(--bg-primary)" : "var(--text-primary)",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div style={CHART_WRAPPER_STYLE}>
        <ResponsiveContainer>
          <LineChart data={chartData} margin={CHART_MARGIN}>
            <CartesianGrid stroke="#D7D2CB" strokeDasharray="2 4" />
            <XAxis dataKey="dateLabel" stroke="#5C5A55" fontSize={11} />
            <YAxis stroke="#5C5A55" fontSize={11} />
            <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} />
            {metrics.map((m) => (
              <Line
                key={m.key}
                type="monotone"
                dataKey={m.key}
                name={m.label}
                stroke={m.color}
                strokeWidth={2}
                dot={LINE_DOT}
                activeDot={LINE_ACTIVE_DOT}
                hide={hidden.has(m.key)}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Custom per-curve legend — each card is a clickable pill that shows
          the line's color, full name, and last measured value. Clicking
          toggles visibility so the user can isolate any single curve. */}
      <div className="mt-4" data-testid="chart-legend">
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)] mb-2">
          Legenda · clicca per mostrare/nascondere
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {metrics.map((m) => {
            const last = [...chartData].reverse().find((r) => r[m.key] != null && r[m.key] !== "");
            const lastVal = last ? last[m.key] : null;
            const isHidden = hidden.has(m.key);
            const unit = UNIT_BY_KEY[m.key] || "cm";
            return (
              <button
                key={m.key}
                type="button"
                data-testid={`legend-item-${m.key}`}
                onClick={() => toggleLine(m.key)}
                className="flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left transition-all"
                style={{
                  borderColor: isHidden ? "var(--border)" : m.color,
                  background: isHidden ? "transparent" : `${m.color}12`,
                  opacity: isHidden ? 0.5 : 1,
                }}
                title={isHidden ? "Mostra questa curva" : "Nascondi questa curva"}
              >
                <span
                  className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                  style={{ background: m.color, boxShadow: isHidden ? "none" : `0 0 0 2px ${m.color}30` }}
                />
                <span className="flex-1 min-w-0">
                  <span className="block text-[11px] font-medium truncate" style={{ color: "var(--text-primary)" }}>
                    {m.label}
                  </span>
                  {lastVal != null && (
                    <span className="block text-[10px] text-[color:var(--text-secondary)] tabular-nums">
                      ultimo · {lastVal}{unit}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
